import React, { useState } from 'react';
import { Platform, TextInputProps, View } from 'react-native';
import styled from 'styled-components/native';

import Button from '../../../components/Button';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';

const StyledAddNoteView = styled.View`
  width: 100%;
`;

const StyledTextInput = styled.TextInput<{
  hasHelperText?: boolean;
  minHeight?: number;
  multiLine?: boolean;
  error?: boolean;
  empty?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 16px;
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : '96px')};
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: ${Platform.OS === 'ios' || Platform.OS === 'web'
    ? '20px 16px'
    : '16px'};
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
` as React.FC<
  TextInputProps & {
    hasHelperText?: boolean;
    minHeight?: number;
    multiLine?: boolean;
    ref?: any;
    error?: boolean;
    empty?: boolean;
  }
>;

interface NotebookFieldConfig {
  placeholder?: string;
}

type NotebookFieldProps<T> = FormElementProps<T, NotebookFieldConfig>;

export const NotebookField = <T extends object>({
  element
}: NotebookFieldProps<T>) => {
  const key = (element.dataSource as keyof T) || element.key;
  const { formValues, setFormValue } = useDynamicFormContext<
    T,
    NotebookFieldConfig
  >();
  const { placeholder } = element.config || {};
  const [height, setHeight] = useState<number>();
  const [addNote, setAddNote] = useState(false);

  return (
    <View>
      <FormElementQuestion element={element} />
      <StyledAddNoteView>
        {formValues[key] || addNote ? (
          <StyledTextInput
            empty={!formValues[key]}
            multiline
            blurOnSubmit={false}
            placeholderTextColor={Colors.placeholderColor}
            placeholder={placeholder}
            minHeight={height}
            value={(formValues[key] as string) || ''}
            onChangeText={text => {
              setFormValue(key, text as any, element);
            }}
            onContentSizeChange={event => {
              setHeight(Math.max(event.nativeEvent.contentSize.height, 196));
            }}
          />
        ) : (
          <Button outlined onPress={() => setAddNote(true)}>
            Add a Note
          </Button>
        )}
      </StyledAddNoteView>
    </View>
  );
};

export const NotebookFieldPlugin: FormElementPlugin<any, NotebookFieldConfig> =
  {
    name: 'Notebook Field',
    type: 'NotebookField',
    dataTypes: ['text'],
    element: NotebookField as React.FC<
      FormElementProps<any, NotebookFieldConfig>
    >
  };
